import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Minus, Plus, Trash2, Timer, ArrowRight, ShoppingCart } from "lucide-react";

export default function CartDrawer({ open, onClose }) {
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = () => {
    if (!user) { onClose(); navigate("/login?role=customer&next=/checkout"); return; }
    onClose();
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={(v) => (!v && onClose())}>
      <SheetContent side="right" className="light-scope bg-white border-l border-slate-200 text-slate-900 w-full sm:max-w-md p-0 flex flex-col pop-in" data-testid="cart-drawer">
        <SheetHeader className="p-5 border-b border-slate-200 flex-row items-center justify-between">
          <SheetTitle className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <ShoppingCart size={18} /> My Cart
            {cart.cart.shop && <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">{cart.cart.shop.name}</span>}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/50">
          {cart.itemsArray.length === 0 && (
            <div className="text-center text-slate-500 py-16" data-testid="cart-empty">
              <ShoppingCart size={32} className="mx-auto text-slate-300" />
              <div className="font-bold text-lg mt-3 text-slate-800">Your cart is empty</div>
              <div className="text-sm">Add fresh picks to get going ⚡</div>
            </div>
          )}
          {cart.cart.shop && cart.itemsArray.length > 0 && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 flex items-center gap-2 text-sm text-emerald-800">
              <Timer size={14} className="text-emerald-600" />
              Delivery in <b>{cart.cart.shop.eta_minutes || 10} minutes</b> from {cart.cart.shop.name}
            </div>
          )}
          {cart.itemsArray.map((it) => (
            <div key={it.product_id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`cart-row-${it.product_id}`}>
              <img src={it.image_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-100" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm leading-tight line-clamp-2">{it.name}</div>
                <div className="text-[11px] text-slate-500">{it.unit} · ₹{it.price}</div>
              </div>
              <div className="inline-flex items-center rounded-lg overflow-hidden border border-emerald-500">
                <button onClick={() => cart.decrement(it.product_id)} className="ls-step-btn"><Minus size={12} /></button>
                <div className="w-7 h-7 inline-flex items-center justify-center bg-emerald-600 text-white text-xs font-bold">{it.qty}</div>
                <button onClick={() => cart.addItem(it, cart.cart.shop)} className="ls-step-btn"><Plus size={12} /></button>
              </div>
              <button onClick={() => cart.removeItem(it.product_id)} className="text-slate-400 hover:text-rose-500 ml-1" aria-label="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {cart.itemsArray.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm space-y-1.5">
              <Row label="Items total" value={`₹${cart.subtotal.toFixed(2)}`} />
              <Row label="Delivery fee" value={cart.deliveryFee ? `₹${cart.deliveryFee}` : "FREE"} good={!cart.deliveryFee} />
              <div className="h-px bg-slate-100 my-1" />
              <Row label="To pay" value={`₹${cart.total.toFixed(2)}`} big />
              {cart.subtotal < cart.fees.free_delivery_min && cart.deliveryFee > 0 && (
                <div className="mt-1 text-[11px] text-slate-500">Add ₹{(cart.fees.free_delivery_min - cart.subtotal).toFixed(0)} more for FREE delivery</div>
              )}
            </div>
          )}
        </div>

        {cart.itemsArray.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-white">
            <Button
              onClick={checkout}
              className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base"
              data-testid="proceed-checkout-btn"
            >
              Proceed to Checkout · ₹{cart.total.toFixed(2)} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, big, good }) {
  return (
    <div className="flex items-center justify-between">
      <span className={big ? "font-bold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={big ? "font-extrabold text-emerald-700 text-lg" : good ? "text-emerald-600 font-semibold" : "font-semibold text-slate-900"}>{value}</span>
    </div>
  );
}
